import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Card, CardContent, 
  IconButton, Grid, FormControlLabel, Radio, RadioGroup,
  Divider, Alert, Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import CSVImportDialog from './CSVImportDialog';

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation?: string;
}

interface QuizEditorProps {
  questions: QuizQuestion[];
  onQuestionsChange: (questions: QuizQuestion[]) => void;
}

const QuizEditor: React.FC<QuizEditorProps> = ({ questions, onQuestionsChange }) => {
  const [editingQuestion, setEditingQuestion] = useState<QuizQuestion | null>(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);

  const generateId = () => `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleAddQuestion = () => {
    const newQuestion: QuizQuestion = {
      id: generateId(),
      question: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      explanation: ''
    };
    setEditingQuestion(newQuestion);
    setIsAddingQuestion(true);
  };

  const handleCSVImport = (importedQuestions: QuizQuestion[]) => {
    // Merge imported questions with existing questions
    const mergedQuestions = [...questions, ...importedQuestions];
    onQuestionsChange(mergedQuestions);
    setCsvImportOpen(false);
  };

  const handleEditQuestion = (question: QuizQuestion) => {
    setEditingQuestion({ ...question });
    setIsAddingQuestion(false);
  };

  const handleSaveQuestion = () => {
    if (!editingQuestion) return;

    // Validate the question
    if (!editingQuestion.question.trim()) {
      alert('Please enter a question');
      return;
    }

    if (editingQuestion.options.some(option => !option.trim())) {
      alert('Please fill in all answer options');
      return;
    }

    if (isAddingQuestion) {
      // Add new question
      onQuestionsChange([...questions, editingQuestion]);
    } else {
      // Update existing question
      const updatedQuestions = questions.map(q => 
        q.id === editingQuestion.id ? editingQuestion : q
      );
      onQuestionsChange(updatedQuestions);
    }

    setEditingQuestion(null);
    setIsAddingQuestion(false);
  };

  const handleCancelEdit = () => {
    setEditingQuestion(null);
    setIsAddingQuestion(false);
  };

  const handleDeleteQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    onQuestionsChange(updatedQuestions);
  };

  const handleQuestionChange = (field: keyof QuizQuestion, value: any) => {
    if (!editingQuestion) return;
    setEditingQuestion({ ...editingQuestion, [field]: value });
  };

  const handleOptionChange = (index: number, value: string) => {
    if (!editingQuestion) return;
    const newOptions = [...editingQuestion.options];
    newOptions[index] = value;
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };

  const handleCorrectAnswerChange = (value: number) => {
    if (!editingQuestion) return;
    setEditingQuestion({ ...editingQuestion, correct_answer: value });
  };

  const renderQuestionForm = () => {
    if (!editingQuestion) return null;

    return (
      <Card sx={{ mb: 2, border: '2px solid #1976d2' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {isAddingQuestion ? 'Add New Question' : 'Edit Question'}
            </Typography>
            <Box>
              <IconButton onClick={handleSaveQuestion} color="primary" title="Save">
                <SaveIcon />
              </IconButton>
              <IconButton onClick={handleCancelEdit} color="error" title="Cancel">
                <CancelIcon />
              </IconButton>
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Question"
            multiline
            rows={3}
            value={editingQuestion.question}
            onChange={(e) => handleQuestionChange('question', e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Enter your question here..."
          />

          <Typography variant="subtitle1" gutterBottom>
            Answer Options
          </Typography>

          <RadioGroup
            value={editingQuestion.correct_answer}
            onChange={(e) => handleCorrectAnswerChange(parseInt(e.target.value))}
          >
            {editingQuestion.options.map((option, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  value={index}
                  control={<Radio />}
                  label=""
                  sx={{ mr: 1 }}
                />
                <TextField
                  fullWidth
                  label={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Enter option ${index + 1}...`}
                  size="small"
                />
              </Box>
            ))}
          </RadioGroup>

          <TextField
            fullWidth
            label="Explanation (Optional)"
            multiline
            rows={2}
            value={editingQuestion.explanation || ''}
            onChange={(e) => handleQuestionChange('explanation', e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Explain why this is the correct answer..."
          />
        </CardContent>
      </Card>
    );
  };

  const renderQuestionList = () => {
    return questions.map((question, index) => (
      <Card key={question.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom>
                Question {index + 1}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {question.question}
              </Typography>

              <Box sx={{ ml: 2 }}>
                {question.options.map((option, optionIndex) => (
                  <Box key={optionIndex} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Radio
                      checked={optionIndex === question.correct_answer}
                      disabled
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: optionIndex === question.correct_answer ? 'bold' : 'normal',
                        color: optionIndex === question.correct_answer ? 'success.main' : 'text.primary'
                      }}
                    >
                      {option}
                    </Typography>
                    {optionIndex === question.correct_answer && (
                      <Chip 
                        label="Correct" 
                        size="small" 
                        color="success" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                ))}
              </Box>

              {question.explanation && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Explanation:</strong> {question.explanation}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleEditQuestion(question)}
                title="Edit Question"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteQuestion(question.id)}
                color="error"
                title="Delete Question"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>
    ));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Quiz Questions ({questions.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setCsvImportOpen(true)}
            disabled={!!editingQuestion}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddQuestion}
            disabled={!!editingQuestion}
          >
            Add Question
          </Button>
        </Box>
      </Box>

      {questions.length === 0 && !editingQuestion && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No questions added yet. Click "Add Question" to create questions manually or "Import CSV" to upload questions from a file.
        </Alert>
      )}

      {renderQuestionForm()}
      {renderQuestionList()}

      <CSVImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={handleCSVImport}
      />
    </Box>
  );
};

export default QuizEditor; 